import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useAppContext } from 'context/AppContext';
import { makeStyles } from 'styles/util';
import { getShortDateFormat, formatShortDate, parseDate, parseDateTime, formatShortDateTimeSeconds } from 'helpers/DateHelper';
import bookingStatuses from 'enums/bookingStatuses';
import adminBookingStatuses from 'enums/adminBookingStatuses';
import storageGroupCategories from 'enums/storageGroupCategories';
import cancellationReasons from 'enums/cancellationReasons';
import subscriptionEndReasons from 'enums/subscriptionEndReasons';
import bookingItemTypes from 'enums/bookingItemTypes';
import routes from 'routes';
import arrayMutators from 'final-form-arrays';
import { changeValueMutator } from 'helpers/FormHelper';
import { getStorageGroupAndStorageTitle } from 'helpers/StorageSiteHelper';
import { required, mustBeNumber } from 'form/validation';
import goodsTypes from 'enums/goodsTypes';
import paymentProviders from 'enums/paymentProviders';
import { getContact, getBusinessOrPrivateName } from 'helpers/ActorHelper';
import contactTypes from 'enums/contactTypes';
import { createFormBookingItem, createBookingItem } from 'logic/bookingLogic';
import { createBookingPriceInfo, createExtendPeriodBookingPriceInfo } from 'helpers/PriceHelper';
import { validateTimePeriodPriceCollectionFormData } from 'logic/timePeriodPriceLogic';
import strings from 'localization/strings';

import { Link } from 'react-router-dom';
import Form from 'form/Form';
import { TextField, showErrorOnBlur } from 'mui-rff';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import AdminFormWrapper from '../../presentational/AdminFormWrapper';
import AdminFormFields from '../../presentational/AdminFormFields';
import AdminFormButtons from '../../presentational/AdminFormButtons';
import ReadOnlyField from '../../presentational/ReadOnlyField';
import ShowingList from './ShowingList';
import PurchaseList from './PurchaseList';
import NoteList from '../../presentational/NoteList';
import StorageEditor from './StorageEditor';
import BookingItemsEditor from './BookingItemsEditor';
import { Checkboxes } from 'mui-rff';
import BookingActions from './BookingActions';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PeriodBookingPriceChangeInfo from './PeriodBookingPriceChangeInfo';
import ShortDate from 'common/ShortDate';
import ShortDateTime from 'common/ShortDateTime';
import AdminActorSelectorDialog from 'components/adminActorSelector/AdminActorSelectorDialog';
import LinkButton from 'common/LinkButton';
import DatePicker from 'form/DatePicker';
import Amount from 'common/Amount';

const useStyles = makeStyles(({ theme, colors }) => ({
    storageAndPricingAccordion: {
        margin: theme.spacing(2, 0),
        boxShadow: 'none',
        borderTop: `1px solid ${colors.mediumGrey}`,
        borderBottom: `1px solid ${colors.mediumGrey}`
    },
    storageAndPricingAccordionSummary: {
        padding: theme.spacing(0)
    },
    storageAndPricingAccordionDetails: {
        flexDirection: 'column',
        paddingLeft: theme.spacing(0),
        paddingRight: theme.spacing(0)
    },
    tenantActorName: {
        paddingRight: theme.spacing(1)
    }
}));

const EditBookingForm = ({ booking, storageSite, isSubmitting = false, onSubmit, onCancel }) => {
    const classes = useStyles();

    const { appContext } = useAppContext();

    const [tenantActor, setTenantActor] = useState({
        id: booking.tenantActorId,
        name: booking.tenantName,
        contacts: [
            { type: contactTypes.mainEmail.key, value: booking.tenantEmail },
            { type: contactTypes.mainMobileTelephone.key, value: booking.tenantMobileTelephone },
        ]
    });
    const [actorSelectorDialogOpen, setActorSelectorDialogOpen] = useState(false);

    const [initialValues] = useState({
        startDate: formatShortDate(booking.startDate, appContext),
        endDate: formatShortDate(booking.endDate, appContext),
        actualEndDate: formatShortDate(booking.actualEndDate, appContext),
        confirmationDeadline: formatShortDateTimeSeconds(booking.confirmationDeadline, appContext),
        initialPaymentDeadline: formatShortDateTimeSeconds(booking.initialPaymentDeadline, appContext),
        subscriptionReactivationDeadline: formatShortDateTimeSeconds(booking.subscriptionReactivationDeadline, appContext),
        flagged: booking.flagged,
        cancellationReason: booking.cancellationReason,
        cancellationReasonText: booking.cancellationReasonText,
        subscriptionEndReason: booking.subscriptionEndReason,
        minimumBookingLength: booking.minimumBookingLength,
        storageGroupId: booking.storageGroupId,
        storageId: booking.storageId,
        storageSelectorDialog: {
            storageGroupId: undefined,
            storageId: undefined
        },
        storageBookingItem: createFormBookingItem(booking.bookingItems.find(bookingItem => bookingItem.type === bookingItemTypes.storage.key), booking, storageSite, appContext),
        bookingItems: booking.bookingItems
            .filter(bookingItem => bookingItem.type !== bookingItemTypes.storage.key)
            .map(bookingItem => ({
                ...createFormBookingItem(bookingItem, booking, storageSite, appContext),
                isPredefined: bookingItem.referenceId !== undefined
            })),
        tenantActorId: booking.tenantActorId,
        bookingStatus: booking.bookingStatus,
        tenantReferenceText: booking.tenantReferenceText,
        createPostInvoiceForExtendedTimePeriod: true
    });

    const showStorageAndPricingSection =
        [adminBookingStatuses.requested.key, adminBookingStatuses.confirmed.key, adminBookingStatuses.notStarted.key, adminBookingStatuses.active.key, adminBookingStatuses.subscriptionEnding.key]
            .includes(booking.adminBookingStatus);

    const getBookingStatusText = () => {
        if(booking.adminBookingStatus === adminBookingStatuses.canceled.key && booking.canceledBy) {
            return `(by ${booking.canceledBy})`;
        }
        return undefined;
    };

    const handleFormSubmit = formValues => {
        onSubmit({
            ...formValues,
            startDate: parseDate(formValues.startDate, appContext),
            endDate: parseDate(formValues.endDate, appContext),
            actualEndDate: parseDate(formValues.actualEndDate, appContext),
            confirmationDeadline: parseDateTime(formValues.confirmationDeadline, appContext),
            initialPaymentDeadline: parseDateTime(formValues.initialPaymentDeadline, appContext),
            subscriptionReactivationDeadline: parseDateTime(formValues.subscriptionReactivationDeadline, appContext)
        });
    };

    const calculatePreviousPeriodBookingPrice = () => {
        if(booking.subscriptionBooking) {
            return undefined;
        }
        const amounts = createBookingPriceInfo(booking, booking.bookingItems, undefined, appContext).wholePeriod.amounts;
        return amounts.totalAmount + amounts.totalVat;
    };

    const calculateNewPeriodBookingPrice = values => {
        if(booking.subscriptionBooking) {
            return undefined;
        }
        const amounts = createBookingPriceInfo(booking, getBookingItemsIncludingStorageBookingItem(values), undefined, appContext).wholePeriod.amounts;
        return amounts.totalAmount + amounts.totalVat;
    };

    const getStorageAndPricingTitle = values => {
        const storageGroup = storageSite.storageGroups.find(o => o.id === values.storageGroupId);
        const storage = storageGroup.storages.find(o => o.id === values.storageId) ?? { title: 'Not selected' };
        return getStorageGroupAndStorageTitle(storage, storageGroup, appContext);
    };

    const getBookingItemsIncludingStorageBookingItem = values => {
        return [createBookingItem(values.storageBookingItem, storageSite, appContext)]
            .concat(values.bookingItems.map(o => createBookingItem(o, storageSite, appContext)));
    };

    const handleActorSelectorDialogClose = () => {
        setActorSelectorDialogOpen(false);
    };

    const handleChangeTenantActorClick = () => {
        setActorSelectorDialogOpen(true);
    };

    const getStatuses = () => {
        if (booking.bookingStatus === bookingStatuses.confirmed.key) {
            return {
                status: [booking.bookingStatus, bookingStatuses.requested.key],
                disabled: false
            };
        }
        if (booking.bookingStatus === bookingStatuses.canceled.key) {
            return {
                status: [booking.bookingStatus, bookingStatuses.confirmed.key],
                disabled: false
            };
        }
        return {
            status: [booking.bookingStatus],
            disabled: true
        };
    };

    const canChangeEndDate = () => !booking.subscriptionBooking && booking.bookingStatus !== bookingStatuses.purchased.key;

    const canChangeActualEndDate = () =>
        (!booking.subscriptionBooking && booking.bookingStatus === bookingStatuses.purchased.key)
        ||
        (
            booking.subscriptionBooking &&
            (
                booking.adminBookingStatus === adminBookingStatuses.subscriptionEnding.key ||
                booking.adminBookingStatus === adminBookingStatuses.ended.key
            )
        );

    const showCreatePostInvoiceForExtendedTimePeriodCheckbox = values =>
        values.bookingStatus === bookingStatuses.purchased.key
        &&
        (
            parseDate(values.startDate, appContext) < parseDate(booking.startDate, appContext)
            ||
            (!booking.subscriptionBooking && parseDate(values.endDate, appContext) > parseDate(booking.endDate, appContext))
        );

    const getAmountForExtendedTimePeriod = values => {
        const extendBookingPriceInfo = createExtendPeriodBookingPriceInfo(booking, { startDate: values.startDate, endDate: values.endDate }, appContext);
        return extendBookingPriceInfo.amounts.totalAmount + extendBookingPriceInfo.amounts.totalVat + extendBookingPriceInfo.amounts.roundingCorrection;
    };

    return (
        <AdminFormWrapper isSubmitting={isSubmitting}>
            <Form
                initialValues={initialValues}
                mutators={{
                    ...arrayMutators,
                    changeValue: changeValueMutator
                }}
                validate={values => {
                    const errors = {};
                    let errorMessage = validateTimePeriodPriceCollectionFormData(values.storageBookingItem.timePeriodPrices, appContext);
                    values.bookingItems
                        .forEach(bi => {
                            errorMessage = errorMessage ?? validateTimePeriodPriceCollectionFormData(bi.timePeriodPrices, appContext, bi.type === bookingItemTypes.storage.key /* validateFullCoverage */);
                        });

                    if(errorMessage) {
                        errors.form = errorMessage;
                    }
                    return errors;
                }}
                onSubmit={handleFormSubmit}
            >
                {({ form, values, invalid, handleSubmit }) => {
                    const previousPeriodBookingPrice = calculatePreviousPeriodBookingPrice(values);
                    const newPeriodBookingPrice = calculateNewPeriodBookingPrice(values);

                    const handleActorSelectorDialogOk = actor => {
                        setTenantActor(actor);
                        form.change('tenantActorId', actor.id);
                        setActorSelectorDialogOpen(false);
                    };

                    return (
                        <form onSubmit={handleSubmit}>
                            <AdminFormFields>

                                <ReadOnlyField label="Storage site">
                                    <Link to={routes.storageSiteDetails.replace(':storageSiteId', booking.storageSiteId)} target="_blank">{booking.storageSiteTitle}</Link>
                                </ReadOnlyField>

                                <ReadOnlyField label="Owner">
                                    <Link to={routes.admin.actor.replace(':actorId', booking.ownerActorId)}>{booking.ownerName}</Link>
                                </ReadOnlyField>

                                <ReadOnlyField label="Tenant">
                                    <Link className={classes.tenantActorName} to={routes.admin.actor.replace(':actorId', tenantActor.id)}>{getBusinessOrPrivateName(tenantActor)}</Link>
                                    <LinkButton onClick={handleChangeTenantActorClick}>Change</LinkButton>
                                </ReadOnlyField>

                                <ReadOnlyField label="Tenant contact details">
                                    <a href={'mailto:' + getContact(tenantActor, contactTypes.mainEmail)}>{getContact(tenantActor, contactTypes.mainEmail)}</a>
                                    {getContact(tenantActor, contactTypes.mainMobileTelephone) && ' | '}
                                    {getContact(tenantActor, contactTypes.mainMobileTelephone)}
                                </ReadOnlyField>

                                <ReadOnlyField label="Description">
                                    {booking.description}
                                </ReadOnlyField>

                                {
                                    booking.storageGroupCategory === storageGroupCategories.vehicle.key &&
                                    (
                                        <>
                                            <ReadOnlyField label="GoodsType">
                                                {(goodsTypes[booking.goodsType] ? goodsTypes[booking.goodsType].title : '')}
                                            </ReadOnlyField>
                                            <ReadOnlyField label="RegistrationNumber">
                                                {booking.registrationNumber}
                                            </ReadOnlyField>
                                        </>
                                    )
                                }

                                <ReadOnlyField label="Subscription">
                                    {booking.paymentProviderPortalSubscriptionUrl ? <a href={booking.paymentProviderPortalSubscriptionUrl} target="_blank">{booking.paymentProviderSubscriptionId}</a> : (booking.subscriptionBooking ? 'yes' : 'no')}
                                </ReadOnlyField>

                                {
                                    booking.paymentProvider &&
                                    (
                                        <ReadOnlyField label="Payment provider">
                                            {paymentProviders[booking.paymentProvider].providerName}
                                        </ReadOnlyField>
                                    )
                                }

                                {
                                    booking.takeoverTenantId &&
                                    (
                                        <ReadOnlyField label="Takeover booking">
                                            <Link to={routes.admin.takeover.replace(':takeoverId', booking.takeoverId)}>{booking.takeoverTenantId}</Link>
                                        </ReadOnlyField>
                                    )
                                }

                                <ReadOnlyField label="Booking request created time">
                                    <ShortDateTime value={booking.createdTime}/>
                                </ReadOnlyField>

                                <DatePicker
                                    name="startDate"
                                    label="Start date"
                                    formatFunc={getShortDateFormat}
                                    inputVariant="outlined"
                                    showError={showErrorOnBlur}
                                    fieldProps={{ validate: required }}
                                />

                                {
                                    !canChangeEndDate() &&
                                    (
                                        <ReadOnlyField label="End date">
                                            <ShortDate value={booking.endDate} defaultString="until further"/>
                                        </ReadOnlyField>
                                    )
                                }
                                {
                                    canChangeEndDate() &&
                                    (
                                        <DatePicker
                                            name="endDate"
                                            label="End date"
                                            formatFunc={getShortDateFormat}
                                            inputVariant="outlined"
                                            showError={showErrorOnBlur}
                                            fieldProps={{ validate: required }}
                                        />
                                    )
                                }
                                {
                                    booking.extendedByBookingIds.map(extendedByBookingId => (
                                        <ReadOnlyField key={extendedByBookingId} label="Extended by booking ID">
                                            <Link to={routes.admin.booking.replace(':bookingId', extendedByBookingId)}>
                                                {extendedByBookingId}
                                            </Link>
                                        </ReadOnlyField>
                                    ))
                                }
                                {
                                    booking.extendsBookingId &&
                                    (
                                        <ReadOnlyField label="Extends booking ID">
                                            <Link to={routes.admin.booking.replace(':bookingId', booking.extendsBookingId)}>
                                                {booking.extendsBookingId}
                                            </Link>
                                        </ReadOnlyField>
                                    )
                                }
                                {
                                    showCreatePostInvoiceForExtendedTimePeriodCheckbox(values) &&
                                    (
                                        <Checkboxes
                                            name="createPostInvoiceForExtendedTimePeriod"
                                            data={{
                                                label: <>Create invoice for extended time period: <Amount value={getAmountForExtendedTimePeriod(values)} currency={booking.currency}/></>,
                                                value: true
                                            }}
                                        />
                                    )
                                }
                                {
                                    canChangeActualEndDate() &&
                                    (
                                        <DatePicker
                                            name="actualEndDate"
                                            label="Actual end date"
                                            formatFunc={getShortDateFormat}
                                            inputVariant="outlined"
                                            showError={showErrorOnBlur}
                                        />
                                    )
                                }

                                <TextField
                                    name="minimumBookingLength"
                                    label="Minimum booking length"
                                    variant="outlined"
                                    required
                                    fieldProps={{ validate: mustBeNumber }}
                                />

                                <TextField
                                    select
                                    name="bookingStatus"
                                    label="Status"
                                    variant="outlined"
                                    disabled={getStatuses().disabled}
                                >
                                    {
                                        getStatuses().status.map(status => (
                                            <MenuItem key={status} value={status}>
                                                {status} {getBookingStatusText()}
                                            </MenuItem>
                                        ))
                                    }
                                </TextField>

                                <ReadOnlyField label="Lifecycle">
                                    {booking.adminBookingStatus}
                                </ReadOnlyField>

                                {
                                    booking.subscriptionBooking &&
                                    ([adminBookingStatuses.subscriptionEnding.key, adminBookingStatuses.ended.key].includes(booking.adminBookingStatus)) &&
                                    booking.subscriptionEndReason !== subscriptionEndReasons.storageChanged.key &&
                                    (
                                        <>
                                            <TextField
                                                select
                                                name="subscriptionEndReason"
                                                label="Subscription end reason"
                                                variant="outlined"
                                            >
                                                {
                                                    Object.values(subscriptionEndReasons).map(o => (
                                                        <MenuItem key={o.key} value={o.key}>{o.title}</MenuItem>
                                                    ))
                                                }
                                            </TextField>

                                            <TextField
                                                name="subscriptionReactivationDeadline"
                                                label="Subscription reactivation deadline"
                                                variant="outlined"
                                                placeholder={strings.shortDateTimeHelperText}
                                            />
                                        </>
                                    )
                                }

                                <ReadOnlyField label="Updated">
                                    <ShortDateTime value={booking.updatedTime}/>
                                </ReadOnlyField>

                                {
                                    showStorageAndPricingSection &&
                                    (
                                        <Accordion className={classes.storageAndPricingAccordion}>
                                            <AccordionSummary className={classes.storageAndPricingAccordionSummary} expandIcon={<ExpandMoreIcon />}>
                                                Storage and pricing: {getStorageAndPricingTitle(values)}
                                            </AccordionSummary>
                                            <AccordionDetails className={classes.storageAndPricingAccordionDetails}>
                                                <StorageEditor
                                                    booking={booking}
                                                    storageSite={storageSite}
                                                />

                                                <BookingItemsEditor
                                                    booking={booking}
                                                    storageSite={storageSite}
                                                />

                                                {
                                                    // SubscriptionBookingPriceChangeInfo has been removed
                                                    // if needed, we can reintroduce it - calculate next invoice price in frontend or backend?
                                                    // for backend, see api/Admin/bookings/{bookingId}/upcomingPurchasePreview
                                                }
                                                {
                                                    !booking.subscriptionBooking && values.bookingStatus === bookingStatuses.purchased.key &&
                                                    (
                                                        <PeriodBookingPriceChangeInfo
                                                            booking={booking}
                                                            previousPrice={previousPeriodBookingPrice}
                                                            newPrice={newPeriodBookingPrice}
                                                        />
                                                    )
                                                }
                                            </AccordionDetails>
                                        </Accordion>
                                    )
                                }

                                <TextField
                                    name="confirmationDeadline"
                                    label="Confirmation deadline"
                                    variant="outlined"
                                    disabled={values.bookingStatus !== bookingStatuses.requested.key}
                                    placeholder={strings.shortDateTimeHelperText}
                                />

                                <TextField
                                    name="initialPaymentDeadline"
                                    label="Initial payment deadline"
                                    variant="outlined"
                                    disabled={values.bookingStatus !== bookingStatuses.confirmed.key}
                                    placeholder={strings.shortDateTimeHelperText}
                                />

                                {
                                    values.bookingStatus === bookingStatuses.canceled.key &&
                                    (
                                        <>
                                            <TextField
                                                select
                                                name="cancellationReason"
                                                label="Cancellation reason"
                                                variant="outlined"
                                            >
                                                {
                                                    Object.values(cancellationReasons).map(o => (
                                                        <MenuItem key={o.key} value={o.key}>{o.title}</MenuItem>
                                                    ))
                                                }
                                            </TextField>

                                            <TextField
                                                name="cancellationReasonText"
                                                label="Cancellation reason notes"
                                                variant="outlined"
                                                maxLength={1024}
                                                multiline
                                                rows={3}
                                            />
                                        </>
                                    )
                                }

                                {
                                    booking.agreementUrl &&
                                    (
                                        <ReadOnlyField label="Agreement">
                                            <Link to={booking.agreementUrl} target="_blank">{booking.agreementUrl}</Link>
                                        </ReadOnlyField>
                                    )
                                }

                                <TextField
                                    name="tenantReferenceText"
                                    label="Tenant reference text"
                                    variant="outlined"
                                />

                                <PurchaseList purchases={booking.purchases} />

                                <NoteList notes={booking.notes}/>

                                <ShowingList bookingId={booking.id} showings={booking.showings} />

                                {
                                    // only possible to remove a flag
                                    // flags are added along with notes
                                    booking.flagged &&
                                    (
                                        <Checkboxes
                                            name="flagged"
                                            data={{
                                                label: 'Flagged',
                                                value: true
                                            }}
                                        />
                                    )
                                }
                            </AdminFormFields>

                            <AdminFormButtons>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={invalid}
                                >
                                    Save
                                </Button>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    color="primary"
                                    onClick={onCancel}
                                >
                                    Cancel
                                </Button>
                            </AdminFormButtons>

                            <AdminActorSelectorDialog
                                title="Select new tenant"
                                onOk={handleActorSelectorDialogOk}
                                onClose={handleActorSelectorDialogClose}
                                open={actorSelectorDialogOpen}
                            />
                        </form>
                    );
            }}
            </Form>

            <BookingActions booking={booking} />

        </AdminFormWrapper>
    );
};

EditBookingForm.propTypes = {
    booking: PropTypes.object.isRequired,
    storageSite: PropTypes.object.isRequired,
    isSubmitting: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

export default EditBookingForm;
